import AppBar from '@material-ui/core/AppBar';
import Divider from '@material-ui/core/Divider';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Icon from '@material-ui/core/Icon';
import IconButton from '@material-ui/core/IconButton';
import Paper from '@material-ui/core/Paper';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import TextField from '@material-ui/core/TextField';
import Toolbar from '@material-ui/core/Toolbar';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import React, { useEffect, useState } from 'react';
import { IPermission } from '../../../../@types/employee';
import { ISqlEmployee } from '../../../../@types/modelV2/rights/user/sqlEmployee';
import { IUserUpsert } from '../../../../@types/modelV2/rights/user/userUpsert';
import { useHandleUpsertUser } from '../../../../hooks/query/rights/userQueries';
import appFunctionsService from '../../../../services/appFunctionsService';
import TabPanel from '../../../customComponents/tab/TabPanel';
import Screen from '../../../customComponentV2/screen/Screen';
import UserAreasDialog from './UserAreasDialog';
import UserRightsDialog from './UserRightsDialog';
import UserDamConstructionTypesDialog from './UserDamConstructionTypesDialog';

interface IUserEditDialogProps {
    selectedEmployee : ISqlEmployee | null;
}

const UserEditDialog = (props : IUserEditDialogProps) => {
    const [currentTab, setCurrentTab] = useState(0);

    const [permission, setPermission] = useState<IPermission>({});
    const [name, setName] = useState('');
    const [employeeNumber, setEmployeeNumber] = useState('');
    const [email, setEmail] = useState('');
    const [areas, setAreas] = useState<Array<string>>([]);
    const [damConstructionTypes, setDamConstructionTypes] = useState<Array<number>>([]);

    const { isLoading: isUpsertingUser, mutate: upsertUser } = useHandleUpsertUser();

    useEffect(() => {
        if (props.selectedEmployee) {
            setData(props.selectedEmployee);
        }
    }, [props.selectedEmployee]);

    const setData = (employee: ISqlEmployee) => {
        setName(employee.name ?? '');
        setEmployeeNumber(employee.employeeNumber ?? '');
        setEmail(employee.email ?? '');
        setAreas(employee.areas.map(x => x.name));
        setDamConstructionTypes(employee.damConstructionTypeIds);

        setPermission({
            IsBoreholeAdmin: employee.isBoreholeAdmin,
            IsBoreholeExport: employee.isBoreholeExport,
            IsBoreholeCreateAdmin: employee.isBoreholeCreateAdmin,
            IsBoreholeTester: employee.isBoreholeTester,
            IsBoreholeUser: employee.isBoreholeUser,
            IsBoreholeYieldAdjuster: employee.isBoreholeYieldAdjuster,
            IsBoreholeFlowMeter: employee.isBoreholeFlowMeter,
            IsEmployeeAdmin: employee.isEmployeeAdmin,
            IsRiverAdmin: employee.isRiverAdmin,
            IsRiverTester: employee.isRiverTester,
            IsRiverUser: employee.isRiverUser,
            IsTransformerAdmin: employee.isTransformerAdmin,
            IsTransformerLpuTester: employee.isTransformerLpuTester,
            IsTransformerSpuTester: employee.isTransformerSpuTester,
            IsTransformerLpuTariff: employee.isTransformerLpuTariff,
            IsTransformerSpuTariff: employee.isTransformerSpuTariff, 
            IsTransformerUser: employee.isTransformerUser,
            IsWeatherStationAdmin: employee.isWeatherStationAdmin,
            IsWeatherStationTester: employee.isWeatherStationTester,
            IsWeatherStationUser: employee.isWeatherStationUser,
            IsMasterDataAdmin: employee.isMasterDataAdmin,
            IsDivisionAdmin: employee.isDivisionAdmin,
            IsBoreholeMasterDataAdmin: employee.isBoreholeMasterDataAdmin,
            IsTransformerBudgetAdmin: employee.isTransformerBudgetAdmin,
            IsTransformerTariffAdmin: employee.isTransformerTariffAdmin,
            IsTransformerMasterDataAdmin: employee.isTransformerMasterDataAdmin,
            IsDamAdmin: employee.isDamAdmin,
            IsDamTester: employee.isDamTester,
            IsDamRegReportUser: employee.isDamRegReportUser,
            IsDamSafetyReportUser: employee.isDamSafetyReportUser,
            IsDamUser: employee.isDamUser,
            IsPumpingStationAdmin: employee.isPumpingStationAdmin,
            IsPumpingStationCreateAdmin: employee.isPumpingStationCreateAdmin,
            IsPumpingStationTester: employee.isPumpingStationTester,
            IsPumpingStationUser: employee.isPumpingStationUser,
            IsTransformerExport: employee.isTransformerExport,
            IsWeatherExport: employee.isWeatherExport,
            IsRiverExport: employee.isRiverExport,
            IsPumpingStationExport: employee.isPumpingStationExport,
            IsCompanyAdmin: employee.isCompanyAdmin,
            IsTransformerMunicipalityTester: employee.isTransformerMunicipalityTester,
            IsTransformerPrepaidTester: employee.isTransformerPrepaidTester,
            IsMunicipalityInvoice: employee.isMunicipalityInvoice,
            IsSPSInvoice: employee.isSPSInvoice,
            IsFlowmeterAdmin: employee.isFlowmeterAdmin,
            IsFlowmeterCreateAdmin: employee.isFlowmeterCreateAdmin,
            IsFlowmeterExport: employee.isFlowmeterExport,
            IsFlowmeterTester: employee.isFlowmeterTester,
            IsFlowmeterUser: employee.isFlowmeterUser,
            IsFillingPointAdmin: employee.isFillingPointAdmin,
            IsFillingPointUser: employee.isFillingPointUser,
            IsFillingPointTester: employee.isFillingPointTester,
            IsRainMeterUser: employee.isRainMeterUser,
            IsRainMeterMeasurementUser: employee.isRainMeterMeasurementUser,
            IsRainMeterAdmin: employee.isRainMeterAdmin,
            IsValueAddedTaxAdmin: employee.isValueAddedTaxAdmin,
            IsGroundWaterZoneAdmin: employee.isGroundWaterZoneAdmin,
            IsDevelopmentToolsAdmin: employee.isDevelopmentToolsAdmin,
        });
    };

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!props) return;

        const upsert : IUserUpsert = {
            id: props.selectedEmployee?.id ?? 0,
            uid: props.selectedEmployee?.uid ?? null,
            guid: props.selectedEmployee?.guid ?? '',
            email: props.selectedEmployee?.email ?? '',
            employeeNumber: employeeNumber,
            password: '',
            name: name,
            areaNames: areas,
            damConstructionTypeIds: damConstructionTypes,
            isActive: true,

            isBoreholeAdmin: permission.IsBoreholeAdmin ?? false,
            isBoreholeExport: permission.IsBoreholeExport ?? false,
            isBoreholeCreateAdmin: permission.IsBoreholeCreateAdmin ?? false,
            isBoreholeUser: permission.IsBoreholeUser ?? false,
            isBoreholeTester: permission.IsBoreholeTester ?? false,
            isBoreholeYieldAdjuster: permission.IsBoreholeYieldAdjuster ?? false,
            isBoreholeFlowMeter: permission.IsBoreholeFlowMeter ?? false,
            isTransformerAdmin: permission.IsTransformerAdmin ?? false,
            isTransformerUser: permission.IsTransformerUser ?? false,
            isTransformerExport: permission.IsTransformerExport ?? false,
            isTransformerLpuTester: permission.IsTransformerLpuTester ?? false,
            isTransformerSpuTester: permission.IsTransformerSpuTester ?? false,
            isTransformerLpuTariff: permission.IsTransformerLpuTariff ?? false,
            IsTransformerSpuTariff: permission.IsTransformerSpuTariff ?? false,
            isWeatherStationAdmin: permission.IsWeatherStationAdmin ?? false,
            isWeatherStationUser: permission.IsWeatherStationUser ?? false,
            isWeatherStationTester: permission.IsWeatherStationTester ?? false,
            isWeatherExport: permission.IsWeatherExport ?? false,
            isRiverAdmin: permission.IsRiverAdmin ?? false,
            isRiverUser: permission.IsRiverUser ?? false,
            isRiverTester: permission.IsRiverTester ?? false,
            isRiverExport: permission.IsRiverExport ?? false,
            
            isEmployeeAdmin: permission.IsEmployeeAdmin ?? false,
            isMasterDataAdmin: permission.IsMasterDataAdmin ?? false,
            isDivisionAdmin: permission.IsDivisionAdmin ?? false,
            isBoreholeMasterDataAdmin: permission.IsBoreholeMasterDataAdmin ?? false,
            isTransformerTariffAdmin: permission.IsTransformerTariffAdmin ?? false,
            isTransformerBudgetAdmin: permission.IsTransformerBudgetAdmin ?? false,
            isTransformerMasterDataAdmin: permission.IsTransformerMasterDataAdmin ?? false,
            isDamAdmin: permission.IsDamAdmin ?? false,
            isDamUser: permission.IsDamUser ?? false,
            isDamTester: permission.IsDamTester ?? false,
            isDamRegReportUser: permission.IsDamRegReportUser ?? false,
            isDamSafetyReportUser: permission.IsDamSafetyReportUser ?? false,
            isDamExport: permission.IsDamExport ?? false,
            isPumpingStationAdmin: permission.IsPumpingStationAdmin ?? false,
            isPumpingStationCreateAdmin: permission.IsPumpingStationCreateAdmin ?? false,
            isPumpingStationUser: permission.IsPumpingStationUser ?? false,
            isPumpingStationTester: permission.IsPumpingStationTester ?? false,
            isPumpingStationExport: permission.IsPumpingStationExport ?? false,
            isCompanyAdmin: permission.IsCompanyAdmin ?? false,
            isTransformerMunicipalityTester: permission.IsTransformerMunicipalityTester ?? false,
            isTransformerPrepaidTester: permission.IsTransformerPrepaidTester ?? false,
            isMunicipalityInvoice: permission.IsMunicipalityInvoice ?? false,
            isSPSInvoice: permission.IsSPSInvoice ?? false,
            isFlowmeterAdmin: permission.IsFlowmeterAdmin ?? false,
            isFlowmeterExport: permission.IsFlowmeterExport ?? false,
            isFlowmeterCreateAdmin: permission.IsFlowmeterCreateAdmin ?? false,
            isFlowmeterUser: permission.IsFlowmeterUser ?? false,
            isFlowmeterTester: permission.IsFlowmeterTester ?? false,
            isFillingPointAdmin: permission.IsFillingPointAdmin ?? false,
            isFillingPointUser: permission.IsFillingPointUser ?? false,
            isFillingPointTester: permission.IsFillingPointTester ?? false,
            isRainMeterUser: permission.IsRainMeterUser ?? false,
            isRainMeterMeasurementUser: permission.IsRainMeterMeasurementUser ?? false,
            isRainMeterAdmin: permission.IsRainMeterAdmin ?? false,
            isValueAddedTaxAdmin: permission.IsValueAddedTaxAdmin ?? false,
            isGroundWaterZoneAdmin: permission.IsGroundWaterZoneAdmin ?? false,
            isDevelopmentToolsAdmin: permission.IsDevelopmentToolsAdmin ?? false,
        };

        upsertUser(upsert);
    };

    const onNameChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setName(event.currentTarget.value);
    };

    const onTabChange = (event: React.ChangeEvent<unknown>, value: number) => {
        setCurrentTab(value);
    };

    const onPermissionsChange = (permissions: IPermission) => {
        setPermission({ ...permissions });
    };

    const onAreasChange = (newAreas: Array<string>) => {
        setAreas([...newAreas.sort()]);
    };

    const onDamConstructionTypesChange = (newDamConstructionType: Array<number>) => {
        setDamConstructionTypes([...newDamConstructionType.sort()]);
    };

    const onEmployeeNumberChange = (event : React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) : void => {
        setEmployeeNumber(event.currentTarget.value);
    }

    return (
        <Screen
            useAnimatedLoader
            isLoading={isUpsertingUser}>
            <div className='fdc flx1 m10 hfill'>
                <Paper className='fdc flx1 p10 hfill'>
                    <form autoComplete='off' onSubmit={onSubmit} className='fdc flx1 p10 hfill'>
                        <Toolbar color='secondary'>
                            <div className='fdr flx1'>
                                <Typography variant='h5' color='inherit'>
                                    Employee {props.selectedEmployee?.name}
                                </Typography>
                                <span className='flx1' />
                                <Tooltip title='Save'>
                                    <div>
                                        <IconButton color='primary' disabled={!props.selectedEmployee} type='submit' aria-label='Edit'>
                                            <Icon>save</Icon>
                                        </IconButton>
                                    </div>
                                </Tooltip>
                            </div>
                        </Toolbar>
                        <Divider />
                        <AppBar position='static'>
                            <Tabs value={currentTab} onChange={onTabChange} aria-label='user tabs'>
                                <Tab label='Info' />
                                <Tab label='Roles' />
                                <Tab label='Areas' />
                                <Tab label='Dam Construction Types' />
                            </Tabs>
                        </AppBar>
                        <div className='fdc flx1'>
                            <TabPanel value={currentTab} index={0}>
                                <div className='fdc flx1 oya'>
                                    <div className='aic p5 mb10'>
                                        <div className={'aic flx1 p5 mb10 pr20'}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    autoComplete='off'
                                                    id='name'
                                                    label='Name'
                                                    value={name ? name : ''}
                                                    required
                                                    error={!name}
                                                    onChange={onNameChange}
                                                    margin='normal'
                                                    className={'TextField'}
                                                />
                                                {
                                                    !name &&
                                                    <FormHelperText error>Required</FormHelperText>
                                                }
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className='fdr'>
                                        <div className={'aic flx1 p5 mb10 pr20'}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    autoComplete='off'
                                                    id='employeeNumber'
                                                    label='Employee Number'
                                                    value={employeeNumber ? employeeNumber : ''}
                                                    required={!employeeNumber}
                                                    error={!employeeNumber}
                                                    margin='normal'
                                                    className={'TextField'}
                                                    onChange={onEmployeeNumberChange}
                                                />
                                                {
                                                    !employeeNumber &&
                                                    <FormHelperText error>Required</FormHelperText>
                                                }
                                            </FormControl>
                                        </div>
                                        <div className={'aic flx1 p5 mb10 pr20'}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    autoComplete='off'
                                                    id='email'
                                                    label='Email Address'
                                                    value={email ? email : ''}
                                                    required={!employeeNumber && !email}
                                                    error={!employeeNumber && !email}
                                                    margin='normal'
                                                    className={'TextField'}
                                                    disabled
                                                />
                                                {
                                                    !employeeNumber &&
                                                    !email &&
                                                    <FormHelperText error>Required</FormHelperText>
                                                }
                                            </FormControl>
                                        </div>
                                    </div>
                                    <div className='fdr'>
                                        <div className={'flx1 aic p5 mb10 pr20'}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    autoComplete='off'
                                                    id='CreatedOn'
                                                    label='Created On'
                                                    value={props.selectedEmployee?.createdOn ? appFunctionsService.formatDateTime(props.selectedEmployee.createdOn) : ''}
                                                    margin='normal'
                                                    className={'TextField'}
                                                    disabled
                                                />
                                            </FormControl>
                                        </div>
                                        <div className={'flx1 aic p5 mb10 pr20'}>
                                            <FormControl fullWidth>
                                                <TextField
                                                    autoComplete='off'
                                                    id='LastSignIn'
                                                    label='Last Sign In'
                                                    value={props.selectedEmployee?.lastSignIn ? appFunctionsService.formatDateTime(props.selectedEmployee.lastSignIn) : ''}
                                                    margin='normal'
                                                    className={'TextField'}
                                                    disabled
                                                />
                                            </FormControl>
                                        </div>
                                    </div>
                                </div>
                            </TabPanel>
                            <TabPanel value={currentTab} index={1}>
                                <div className='fdc flx1 oya'>
                                    <UserRightsDialog permissions={permission} onChange={onPermissionsChange} />
                                </div>
                            </TabPanel>
                            <TabPanel value={currentTab} index={2}>
                                <div className='fdc flx1 oya'>
                                    <UserAreasDialog value={areas} onChange={onAreasChange} />
                                </div>
                            </TabPanel>
                            <TabPanel value={currentTab} index={3}>
                                <div className='fdc flx1 oya'>
                                    <UserDamConstructionTypesDialog 
                                        user={props.selectedEmployee}
                                        value={damConstructionTypes} 
                                        onChange={onDamConstructionTypesChange} 
                                    />
                                </div>
                            </TabPanel>
                        </div>
                    </form>
                </Paper>
            </div>
        </Screen>
    );
}

export default UserEditDialog;
